import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, deleteDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../api/firebase';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Rating,
  Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const AdminDashboardPage = () => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [professors, setProfessors] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  // Stats calculation
  const stats = {
    pending: reports.filter(r => r.status === 'pending').length,
    reviewed: reports.filter(r => r.status === 'reviewed').length,
    resolved: reports.filter(r => r.status === 'resolved').length,
    total: reports.length
  };

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch reports
        const reportsQuery = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
        const reportsSnapshot = await getDocs(reportsQuery);
        const reportsData = reportsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setReports(reportsData);

        // Fetch professor names
        const professorIds = [...new Set(reportsData.map(r => r.professorId))];
        const professorsMap = {};
        
        for (const profId of professorIds) {
          try {
            const profDoc = await getDoc(doc(db, 'professors', profId));
            if (profDoc.exists()) {
              professorsMap[profId] = profDoc.data().name || profDoc.data().full_name || 'Unknown';
            }
          } catch (err) {
            professorsMap[profId] = 'Unknown';
          }
        }
        
        setProfessors(professorsMap);
      } catch (err) {
        setError(t('Failed to load reports.', 'فشل في تحميل البلاغات.'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoading, user, isAdmin, t]);

  const handleViewReport = (report) => {
    setSelectedReport(report);
    setViewDialogOpen(true);
  };

  const handleDismissReport = async (reportId) => {
    try {
      setActionLoading(true);
      await updateDoc(doc(db, 'reports', reportId), {
        status: 'resolved',
        resolvedBy: user.uid,
        resolvedAt: new Date()
      });
      
      // Update local state
      setReports(prev => prev.map(r => 
        r.id === reportId 
          ? { ...r, status: 'resolved', resolvedBy: user.uid, resolvedAt: new Date() }
          : r
      ));
      
      setViewDialogOpen(false);
    } catch (err) {
      setError(t('Failed to dismiss report.', 'فشل في رفض البلاغ.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!selectedReport) return;
    
    try {
      setActionLoading(true);
      
      // Delete the review
      await deleteDoc(doc(db, 'reviews', selectedReport.reviewId));
      
      // Update report status
      await updateDoc(doc(db, 'reports', selectedReport.id), {
        status: 'resolved',
        resolvedBy: user.uid,
        resolvedAt: new Date()
      });
      
      // Update local state
      setReports(prev => prev.map(r => 
        r.id === selectedReport.id 
          ? { ...r, status: 'resolved', resolvedBy: user.uid, resolvedAt: new Date() }
          : r
      ));
      
      setDeleteDialogOpen(false);
      setViewDialogOpen(false);
    } catch (err) {
      setError(t('Failed to delete review.', 'فشل في حذف التقييم.'));
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: t('Pending', 'قيد الانتظار') },
      reviewed: { color: 'info', label: t('Reviewed', 'تمت المراجعة') },
      resolved: { color: 'success', label: t('Resolved', 'تم الحل') }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Show access denied if not admin
  if (!authLoading && (!user || !isAdmin)) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 2, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('Access Denied. Admin privileges required.', 'تم رفض الوصول. يلزم صلاحيات المسؤول.')}
          </Alert>
          <Button onClick={() => navigate('/')} variant="contained">
            {t('Return Home', 'العودة للصفحة الرئيسية')}
          </Button>
        </Paper>
      </Container>
    );
  }

  if (loading || authLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DashboardIcon sx={{ fontSize: '2rem', color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            {t('Admin Dashboard', 'لوحة تحكم المسؤول')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={() => navigate('/add-lecturer')}
          sx={{
            px: 3,
            py: 1.5,
            fontSize: '1rem',
            textTransform: 'none'
          }}
        >
          {t('Add Lecturer', 'إضافة محاضر')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('Total Reports', 'إجمالي البلاغات')}
              </Typography>
              <Typography variant="h4">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('Pending', 'قيد الانتظار')}
              </Typography>
              <Typography variant="h4" color="warning.main">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('Reviewed', 'تمت المراجعة')}
              </Typography>
              <Typography variant="h4" color="info.main">{stats.reviewed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                {t('Resolved', 'تم الحل')}
              </Typography>
              <Typography variant="h4" color="success.main">{stats.resolved}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reports Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Status', 'الحالة')}</TableCell>
                <TableCell>{t('Professor', 'المحاضر')}</TableCell>
                <TableCell>{t('Reason', 'السبب')}</TableCell>
                <TableCell>{t('Reported', 'تاريخ البلاغ')}</TableCell>
                <TableCell>{t('Actions', 'الإجراءات')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('No reports found.', 'لا توجد بلاغات.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>{getStatusChip(report.status)}</TableCell>
                    <TableCell>{professors[report.professorId] || 'Unknown'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                        {report.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {report.createdAt?.toDate 
                        ? report.createdAt.toDate().toLocaleDateString(isArabic ? 'ar-SA' : 'en-US')
                        : t('Unknown', 'غير معروف')}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewReport(report)}
                        sx={{ mr: 1 }}
                      >
                        {t('View', 'عرض')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Report Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('Report Details', 'تفاصيل البلاغ')}
        </DialogTitle>
        <DialogContent>
          {selectedReport && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('Status', 'الحالة')}
              </Typography>
              <Box sx={{ mb: 2 }}>{getStatusChip(selectedReport.status)}</Box>

              <Typography variant="subtitle2" gutterBottom>
                {t('Professor', 'المحاضر')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {professors[selectedReport.professorId] || 'Unknown'}
              </Typography>

              <Typography variant="subtitle2" gutterBottom>
                {t('Reason', 'السبب')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {selectedReport.reason}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>
                {t('Review Snapshot', 'لقطة التقييم')}
              </Typography>
              {selectedReport.reviewSnapshot && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Rating 
                    value={selectedReport.reviewSnapshot.overallRating || 0} 
                    readOnly 
                    precision={0.1}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2">
                    {selectedReport.reviewSnapshot.comment || t('No comment', 'لا يوجد تعليق')}
                  </Typography>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                {t('Reported on', 'تم الإبلاغ في')}: {
                  selectedReport.createdAt?.toDate 
                    ? selectedReport.createdAt.toDate().toLocaleString(isArabic ? 'ar-SA' : 'en-US')
                    : t('Unknown', 'غير معروف')
                }
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setViewDialogOpen(false)}
            disabled={actionLoading}
          >
            {t('Close', 'إغلاق')}
          </Button>
          {selectedReport?.status !== 'resolved' && (
            <>
              <Button
                onClick={() => handleDismissReport(selectedReport.id)}
                disabled={actionLoading}
                startIcon={<CancelIcon />}
                color="inherit"
              >
                {t('Dismiss', 'رفض')}
              </Button>
              <Button
                onClick={() => {
                  setViewDialogOpen(false);
                  setDeleteDialogOpen(true);
                }}
                disabled={actionLoading}
                startIcon={<DeleteIcon />}
                color="error"
                variant="contained"
              >
                {t('Delete Review', 'حذف التقييم')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {t('Delete Review?', 'حذف التقييم؟')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'Are you sure you want to delete this review? This action cannot be undone.',
              'هل أنت متأكد من حذف هذا التقييم؟ لا يمكن التراجع عن هذا الإجراء.'
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
            {t('Cancel', 'إلغاء')}
          </Button>
          <Button 
            onClick={handleDeleteReview} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {t('Delete', 'حذف')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminDashboardPage;

